/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React from 'react'

import {
  EuiFlyout,
  EuiFlyoutHeader,
  EuiFlyoutBody,
  EuiTitle,
  EuiFlyoutFooter,
  EuiButtonEmpty,
  EuiText,
  EuiSpacer,
  EuiFlexGroup,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { LineItem } from '@modules/billing-api/customTypes'

import LineItemStatus from '../LineItemStatus'

import ActivitySection from './ActivitySection'
import DetailsSection from './DetailsSection'
import PricesSection from './PricesSection'
import MarketplaceSection from './MarketplaceSection'

interface Props {
  lineItem: LineItem
  onClose: () => void
}

const LineItemFlyout = ({ onClose, lineItem }: Props) => (
  <EuiFlyout ownFocus={true} onClose={onClose} size='520px'>
    <EuiFlyoutHeader hasBorder={true}>
      <EuiFlexGroup alignItems='center' gutterSize='s'>
        <EuiTitle size='m'>
          <h2>
            <FormattedMessage id='line-item.details-flyout.title' defaultMessage='Line item' />
          </h2>
        </EuiTitle>
        <LineItemStatus type='badge' lineItem={lineItem} color='default' />
      </EuiFlexGroup>
      <EuiSpacer size='s' />
      <EuiText component='span' size='s' color='subdued'>
        {lineItem.id}
      </EuiText>
    </EuiFlyoutHeader>
    <EuiFlyoutBody>
      <EuiFlexGroup gutterSize='m' direction='column'>
        <ActivitySection lineItem={lineItem} />
        <DetailsSection lineItem={lineItem} />
        <PricesSection lineItem={lineItem} />
        <MarketplaceSection lineItem={lineItem} />
      </EuiFlexGroup>
    </EuiFlyoutBody>
    <EuiFlyoutFooter>
      <EuiButtonEmpty onClick={onClose}>
        <FormattedMessage id='line-item.details-flyout.close' defaultMessage='Close' />
      </EuiButtonEmpty>
    </EuiFlyoutFooter>
  </EuiFlyout>
)

export default LineItemFlyout
